/**
 * ScreenplayDocument v1 — the STORED FORM of a screenplay.
 *
 * Lexical is only the editing surface; this flat, typed element array is the
 * single source of truth that Phase 3 (save/load, Fountain import), Phase 5
 * (PDF export) and everything after build on. Approved 2026-07-07:
 *   1. flat ordered array (scenes are derived, never stored as a tree)
 *   2. stable per-element ids
 *   3. text stored as typed — uppercasing is presentation, never data
 *   4. unknown types / bad versions are HARD parse errors, never dropped
 *   5. pure module: no Lexical imports, parse(serialize(x)) ≡ x
 *
 * This module must stay dependency-free (no React, no Lexical) so the same
 * code can run in the backend of a future phase or a CLI Fountain converter.
 *
 * ⚠ MATCHED PAIR: parseDocument here and _validate_screenplay in
 * backend/server.py enforce the same STRUCTURAL contract and MUST change
 * together. Drift between them is a data-integrity risk: a document the
 * server accepts that this module hard-rejects bricks the writer's script
 * behind the "couldn't be loaded" panel; one this module produces but the
 * server rejects makes every save fail.
 *
 * Deliberate asymmetry (documented, not drift): the server ADDITIONALLY
 * enforces size budgets — SCREENPLAY_ELEMENT_CAP (20k elements) and
 * SCREENPLAY_TOTAL_TEXT_CAP (2M chars, text + ids) return 413. This module
 * mirrors the per-element limits (ELEMENT_TEXT_CAP, ELEMENT_ID_CAP) so a
 * single pathological element fails loudly client-side, but whole-document
 * budgets are the server's job.
 */

export const SCREENPLAY_VERSION = 1;

// Per-element limits — matched with the server's SCREENPLAY_ELEMENT_TEXT_CAP
// and SCREENPLAY_ID_CAP.
export const ELEMENT_TEXT_CAP = 100_000;
export const ELEMENT_ID_CAP = 64;

// Document-level budgets — mirror the server's SCREENPLAY_ELEMENT_CAP /
// SCREENPLAY_TOTAL_TEXT_CAP by VALUE (same asymmetry as above: parseDocument
// and serializeDocument deliberately do NOT enforce these — they're the
// server's job on every save). Exported here so Fountain import — the other
// place a single write can legitimately produce a huge document — can fail
// loudly client-side, before ever reaching the network, instead of parsing
// the whole file only to have the save 413.
export const ELEMENT_CAP = 20_000;
export const TOTAL_TEXT_CAP = 2_000_000;

export const ELEMENT_TYPES = Object.freeze([
  'scene_heading',
  'action',
  'character',
  'parenthetical',
  'dialogue',
  'transition',
  'shot',
]);

const ELEMENT_TYPE_SET = new Set(ELEMENT_TYPES);

/** Human labels for toolbars and error messages. */
export const ELEMENT_LABELS = Object.freeze({
  scene_heading: 'Scene Heading',
  action: 'Action',
  character: 'Character',
  parenthetical: 'Parenthetical',
  dialogue: 'Dialogue',
  transition: 'Transition',
  shot: 'Shot',
});

/**
 * Thrown for any structurally invalid document. Carries a writer-readable
 * `reason` so the UI can fail gracefully ("this screenplay couldn't be
 * loaded: …") while the code path fails loudly.
 */
export class ScreenplayParseError extends Error {
  constructor(reason) {
    super(`Screenplay document is invalid: ${reason}`);
    this.name = 'ScreenplayParseError';
    this.reason = reason;
  }
}

/**
 * Mint a stable element id ('el_' + 16 hex). Exported so the editor nodes can
 * assign an id at node creation — ids must be minted ONCE and stay stable,
 * never re-minted at serialize time. 8 random bytes: a feature screenplay is
 * 5-10k elements, and 4 bytes gave ~1% birthday-collision odds per document.
 */
export function mintElementId() {
  const bytes = new Uint8Array(8);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return `el_${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

/** Create a new element of `type` with a fresh stable id. */
export function createElement(type, text = '') {
  if (!ELEMENT_TYPE_SET.has(type)) {
    throw new ScreenplayParseError(`unknown element type "${type}"`);
  }
  if (typeof text !== 'string') {
    throw new ScreenplayParseError(`element text must be a string, got ${typeof text}`);
  }
  return { id: mintElementId(), type, text };
}

function validateElement(el, index) {
  if (el === null || typeof el !== 'object' || Array.isArray(el)) {
    throw new ScreenplayParseError(`element ${index} is not an object`);
  }
  if (typeof el.id !== 'string' || !el.id) {
    throw new ScreenplayParseError(`element ${index} is missing its id`);
  }
  if (el.id.length > ELEMENT_ID_CAP) {
    throw new ScreenplayParseError(
      `element ${index} id exceeds ${ELEMENT_ID_CAP} characters`
    );
  }
  if (!ELEMENT_TYPE_SET.has(el.type)) {
    // NEVER silently drop a writer's text — surface exactly what we can't read.
    throw new ScreenplayParseError(
      `element ${index} has unknown type "${String(el.type)}"`
    );
  }
  if (typeof el.text !== 'string') {
    throw new ScreenplayParseError(
      `element ${index} ("${el.type}") has non-string text`
    );
  }
  if (el.text.length > ELEMENT_TEXT_CAP) {
    throw new ScreenplayParseError(
      `element ${index} text exceeds ${ELEMENT_TEXT_CAP} characters`
    );
  }
}

// Lone UTF-16 surrogates (bad paste, exotic IME states) survive in JS
// strings but cannot be stored: the server rejects them because they are
// not UTF-8-encodable (they would 500 inside BSON otherwise). Serialization
// replaces them with U+FFFD — the one sanctioned mutation on the way out,
// applied only to malformed code units that no font can render anyway.
const LONE_SURROGATE_RE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

function sanitizeSurrogates(text) {
  return text.replace(LONE_SURROGATE_RE, '�');
}

/** Ids key future merge/diff/versioning — duplicates are corruption, not data. */
function assertUniqueIds(elements) {
  const seen = new Set();
  for (let i = 0; i < elements.length; i += 1) {
    if (seen.has(elements[i].id)) {
      throw new ScreenplayParseError(
        `element ${i} reuses id "${elements[i].id}" — element ids must be unique`
      );
    }
    seen.add(elements[i].id);
  }
}

/**
 * elements -> ScreenplayDocument. Validates on the way OUT too: a corrupt
 * in-memory state must never be persisted looking healthy.
 */
export function serializeDocument(elements) {
  if (!Array.isArray(elements)) {
    throw new ScreenplayParseError('elements must be an array');
  }
  elements.forEach(validateElement);
  assertUniqueIds(elements);
  return {
    version: SCREENPLAY_VERSION,
    elements: elements.map((el) => ({
      id: el.id,
      type: el.type,
      text: sanitizeSurrogates(el.text),
    })),
  };
}

/**
 * ScreenplayDocument (parsed JSON) -> elements array.
 * Hard-errors (ScreenplayParseError) on anything structurally wrong;
 * the caller renders the graceful "couldn't be loaded" state from `.reason`.
 */
export function parseDocument(doc) {
  if (doc === null || typeof doc !== 'object' || Array.isArray(doc)) {
    throw new ScreenplayParseError('document is not an object');
  }
  if (doc.version === undefined) {
    throw new ScreenplayParseError('document has no version field');
  }
  if (doc.version !== SCREENPLAY_VERSION) {
    throw new ScreenplayParseError(
      `document version ${String(doc.version)} is not supported ` +
      `(this build reads version ${SCREENPLAY_VERSION})`
    );
  }
  if (!Array.isArray(doc.elements)) {
    throw new ScreenplayParseError('document elements is not an array');
  }
  doc.elements.forEach(validateElement);
  assertUniqueIds(doc.elements);
  // Unknown extra fields are deliberately stripped here (forward-compat:
  // core id/type/text always survive; anything else must bump the version).
  return doc.elements.map((el) => ({ id: el.id, type: el.type, text: el.text }));
}

/** A valid empty document — the Phase 1 starting state. */
export function emptyDocument() {
  return { version: SCREENPLAY_VERSION, elements: [] };
}
